import { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { adminContent } from 'services/api/adminContentClient';
import type { AdminUserSummary } from 'types';

type Props = {
  account: AdminUserSummary | null;
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (account: AdminUserSummary | null) => void;
};

const accountLabel = (account: AdminUserSummary) =>
  [account.first_name, account.last_name].filter(Boolean).join(' ') || account.email;

export default function PerformanceAccountFilter({ account, query, onQueryChange, onSelect }: Props) {
  const [results, setResults] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const search = query.trim();

  useEffect(() => {
    let active = true;
    setResults([]);
    setError(false);
    setLoading(search.length >= 2);
    if (search.length < 2) return;
    const timer = window.setTimeout(() => {
      adminContent.cohorts.userSearch({ search, limit: 10 })
        .then(({ data }) => { if (active) setResults(data); })
        .catch(() => { if (active) setError(true); })
        .finally(() => { if (active) setLoading(false); });
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [search, retry]);

  return (
    <div className="w-full space-y-2 text-sm sm:w-80">
      <label className="block space-y-2">
        <span className="block">Account</span>
        <Input
          type="search" aria-label="Search accounts" placeholder="Search by first name, last name, or email"
          autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          value={query} onChange={(event) => onQueryChange(event.target.value)}
        />
      </label>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 break-words">{account ? `${accountLabel(account)} (${account.email})` : 'All accounts'}</span>
        {(account || query) && <Button type="button" size="auto" variant="secondary" onClick={() => onSelect(null)}>All accounts</Button>}
      </div>
      {search && !account && <p className="text-xs text-muted-foreground">{search.length < 2 ? 'Enter at least 2 characters to search.' : 'Choose a matching account before applying filters.'}</p>}
      {loading && <p role="status">Searching accounts...</p>}
      {error && <div><p role="alert">Unable to search accounts.</p><Button type="button" size="auto" variant="secondary" onClick={() => setRetry((value) => value + 1)}>Retry account search</Button></div>}
      {!loading && !error && search.length >= 2 && !results.length && <p role="status">No matching accounts.</p>}
      {!loading && results.length > 0 && <ul aria-label="Matching accounts" className="max-h-52 overflow-auto rounded-xl border border-input">
        {results.map((result) => <li key={result.id}>
          <button type="button" className="w-full break-words px-3 py-2 text-left hover:bg-muted focus-visible:outline focus-visible:outline-2" onClick={() => onSelect(result)}>
            <span className="block font-medium">{accountLabel(result)}</span>
            <span className="block text-muted-foreground">{result.email}</span>
          </button>
        </li>)}
      </ul>}
      {!loading && results.length === 10 && <p className="text-xs text-muted-foreground">Showing the first 10 matches. Refine your search if needed.</p>}
    </div>
  );
}
